import GameMode from './GameMode.js';
import {
  ASTEROID_INITIAL_COUNT,
  DEATHMATCH_KILL_BONUS,
  SHIELD_DEFAULT_COUNT
} from 'asteroids-shared/constants';
import { normalizeDeg } from 'asteroids-shared/physics';

export default class DeathMatchMode extends GameMode {
  static get name() { return 'deathmatch'; }

  constructor({ roundDuration = 180000, killBonus = DEATHMATCH_KILL_BONUS, asteroidCount = ASTEROID_INITIAL_COUNT, asteroidsEnabled = true, shieldsEnabled = false } = {}) {
    super()
    this.roundDuration = roundDuration
    this.killBonus = killBonus
    this.asteroidCount = asteroidCount
    this.asteroidsEnabled = asteroidsEnabled !== false
    this.shieldsEnabled = shieldsEnabled === true
    this.startedAt = 0
    this.scores = new Map()
  }

  get shipVsShipCollision() { return true }
  get bulletHitsFriendly() { return true }
  get sharedScore() { return false }
  get initialLives() { return Infinity }
  get respawnDelay() { return 2000 }
  get timedGameMs() { return this.roundDuration }
  get spawnInvulnerabilityDuration() { return 2500 }
  get disableFireDuringSpawnInvulnerability() { return true }

  getSpawnPositions({ worldWidth, worldHeight, playerCount }) {
    const cx = worldWidth / 2
    const cy = worldHeight / 2
    const r = 480
    if (playerCount <= 1) return [{ x: cx, y: cy, rotation: 0 }]

    // Ring slot at angleRad (bottom = π/2, then clockwise); nose aims at map center.
    const slot = angleRad => {
      const x = cx + Math.cos(angleRad) * r
      const y = cy + Math.sin(angleRad) * r
      const dx = cx - x
      const dy = cy - y
      const len = Math.hypot(dx, dy) || 1
      const ω = Math.atan2(-dx / len, -dy / len)
      const rotation = normalizeDeg(-(ω * 180) / Math.PI)
      return { x, y, rotation }
    }

    // Bottom of arena first, then clockwise; matches sorted players in ServerGame.
    const start = Math.PI / 2
    const out = []
    for (let i = 0; i < playerCount; i++) {
      const a = start + (i / playerCount) * Math.PI * 2
      out.push(slot(a))
    }
    return out
  }

  onGameStart(world, players, ctx) {
    this.startedAt = ctx.now()
    this.scores.clear()
    for (const player of players) {
      this.scores.set(player.id, {
        playerId: player.id,
        name: player.name,
        score: 0,
        kills: 0,
        deaths: 0
      })
    }
    if (this.shieldsEnabled) {
      for (const ship of world.ships.values()) {
        ship.shieldMax = SHIELD_DEFAULT_COUNT
        ship.shieldHealth = SHIELD_DEFAULT_COUNT
      }
    }
    if (this.asteroidsEnabled) world.spawnAsteroidWave(this.asteroidCount)
    ctx.broadcastScore({ scoreboard: this.sortedStandings(ctx), reason: null })
  }

  onAsteroidDestroyed(_world, asteroid, byPlayerId, ctx) {
    if (byPlayerId == null) return
    const stats = this.statsFor(byPlayerId)
    stats.score += asteroid.score
    ctx.broadcastScore({ scoreboard: this.sortedStandings(ctx), reason: null })
  }

  onPlayerLeave(_world, playerId, ctx) {
    const stats = this.statsFor(playerId)
    ctx.broadcastScore({ scoreboard: this.sortedStandings(ctx), reason: null })
    return stats
  }

  onShipHit(_world, ship, hitBy, ctx) {
    const victim = this.statsFor(ship.id)
    victim.name = victim.name ?? ship.name
    victim.deaths += 1

    if (hitBy && hitBy !== ship.id) {
      const killer = this.statsFor(hitBy)
      const killerShip = _world.ships.get(hitBy)
      if (killerShip?.name) killer.name = killer.name ?? killerShip.name
      killer.kills += 1
      killer.score += this.killBonus
      const rp = ctx.ramPairIds
      const skipKillLine = rp?.length === 2
        && (ship.id === rp[0] || ship.id === rp[1])
        && (hitBy === rp[0] || hitBy === rp[1])
      if (!skipKillLine) {
        ctx.broadcastHudFeed({
          killerId: hitBy,
          victimId: ship.id,
          method: 'kill',
          scoreDelta: this.killBonus
        })
      }
    }

    ctx.scheduleRespawn(ship.id, this.respawnDelay)
    ctx.broadcastScore({ scoreboard: this.sortedStandings(ctx), reason: null })
  }

  onShipCollide(_world, shipA, shipB, ctx) {
    const pair = [shipA.id, shipB.id]
    pair.sort((a, b) => String(a).localeCompare(String(b)))
    ctx.ramPairIds = pair
    try {
      ctx.hitShip(shipA.id, shipB.id)
      ctx.hitShip(shipB.id, shipA.id)
    } finally {
      ctx.broadcastHudFeed({
        method: 'ram',
        playerA: pair[0],
        playerB: pair[1]
      })
      delete ctx.ramPairIds
    }
  }

  onAllAsteroidsCleared(world, _ctx) {
    if (this.asteroidsEnabled) world.spawnAsteroidWave(this.asteroidCount)
  }

  sortedStandings(ctx) {
    const now = ctx?.now?.() ?? Date.now()
    return [...this.scores.values()]
      .map(entry => ({ ...entry }))
      .sort((a, b) =>
        b.score - a.score
        || b.kills - a.kills
        || a.deaths - b.deaths
        || String(a.playerId).localeCompare(String(b.playerId))
      )
      .map((entry, index) => ({ ...entry, rank: index + 1, at: now }))
  }

  checkGameOver(_world, ctx) {
    const elapsed = ctx.now() - this.startedAt
    if (elapsed < this.timedGameMs) return null
    const standings = this.sortedStandings(ctx)
    return {
      reason: 'time_up',
      winnerId: standings[0]?.playerId ?? null,
      standings,
      returnLobbyCapMs: 30000
    }
  }

  statsFor(playerId) {
    let stats = this.scores.get(playerId)
    if (!stats) {
      stats = { playerId, score: 0, kills: 0, deaths: 0 }
      this.scores.set(playerId, stats)
    }
    return stats
  }
}
